import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Tag,
  message,
  Space,
  Tooltip,
  Typography,
  Row,
  Col,
  Statistic,
  Image,
  Tabs,
} from "antd";
import {
  DollarOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  MoneyCollectOutlined,
} from "@ant-design/icons";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import { LineWave } from "react-loader-spinner";
import AccountsService from "../utils/accountsService";
import CollateralSalesService from "../utils/collateralSalesService";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

const CollateralSales = () => {
  const { company } = useContext(AppContext);
  const [collaterals, setCollaterals] = useState([]);
  const [availableCollaterals, setAvailableCollaterals] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedCollateral, setSelectedCollateral] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState("available");
  const [form] = Form.useForm();
  const token = localStorage.getItem("token");

  const statusColors = {
    "Pending Verification": "orange",
    Active: "green",
    Repossessed: "volcano",
    Sold: "red",
    Released: "blue",
  };

  useEffect(() => {
    fetchData();
  }, [company]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [
        availableCollateralsData,
        soldCollateralsData,
        accountsData,
        loansData,
      ] = await Promise.all([
        CollateralSalesService.getAvailableForSale(company.id, token),
        CollateralSalesService.getSales(company.id, token),
        AccountsService.fetchAccounts(company.id, token),
        axios.get(
          `https://zedlenders.pickmesms.com/items/loans?filter[company]=${company.id}&fields=id,amount,borrower.first_name,borrower.last_name`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        ),
      ]);
      console.log("Available Collaterals:", availableCollateralsData);

      setAvailableCollaterals(availableCollateralsData);
      setCollaterals(soldCollateralsData);
      setAccounts(accountsData);
      setLoans(loansData.data.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSellCollateral = (collateral) => {
    setSelectedCollateral(collateral);
    setIsModalVisible(true);
    form.setFieldsValue({
      collateral_id: collateral.id,
      initial_value: collateral.valuation_amount,
      date_sold: dayjs(),
    });
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setSelectedCollateral(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      setProcessing(true);

      const saleData = {
        collateral_id: selectedCollateral.id,
        sale_amount: values.sale_amount,
        initial_value: selectedCollateral.valuation_amount,
        account_id: values.account_id,
        related_loan_id: values.related_loan_id,
        date_sold: values.date_sold.format("YYYY-MM-DD"),
        summary: values.summary,
        company_id: company.id,
      };

      await CollateralSalesService.recordSale(saleData, token);

      message.success("Collateral sale recorded successfully!");
      handleModalCancel();
      fetchData();
    } catch (error) {
      console.error("Error recording sale:", error);
      message.error("Failed to record collateral sale");
    } finally {
      setProcessing(false);
    }
  };

  const columns = [
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          {record.loan && (
            <div>
              <Text type="secondary">
                Loan: {record.loan.borrower?.first_name}{" "}
                {record.loan.borrower?.last_name}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Initial Value",
      dataIndex: "valuation_amount",
      key: "valuation_amount",
      render: (amount) => (
        <Text strong>ZMW {parseFloat(amount || 0).toLocaleString()}</Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={statusColors[status] || "default"}>{status}</Tag>
      ),
    },
    {
      title: "Registration Date",
      dataIndex: "registration_date",
      key: "registration_date",
      render: (date) => dayjs(date).format("DD/MM/YYYY"),
    },
    {
      title: "Images",
      dataIndex: "documents",
      key: "documents",
      render: (documents) => {
        if (!documents) return <Text type="secondary">No images</Text>;
        
        // Parse documents if it's a string
        let parsedDocuments = documents;
        if (typeof documents === 'string') {
          try {
            parsedDocuments = JSON.parse(documents);
          } catch (e) {
            return <Text type="secondary">No images</Text>;
          }
        }
        
        if (!Array.isArray(parsedDocuments) || parsedDocuments.length === 0)
          return <Text type="secondary">No images</Text>;

        const imageUrls = parsedDocuments.map(
          (doc) => {
            // If doc is a string (Firebase URL), use it directly
            if (typeof doc === 'string') {
              return doc;
            }
            // If doc is an object with directus_files_id, construct the URL
            if (doc.directus_files_id) {
              return `https://zedlenders.pickmesms.com/assets/${doc.directus_files_id}`;
            }
            // Fallback: return doc as is
            return doc;
          }
        );

        return (
          <Image.PreviewGroup>
            {imageUrls.slice(0, 3).map((url, index) => (
              <Image
                key={index}
                width={50}
                height={50}
                src={url}
                style={{ objectFit: "cover", marginRight: 8 }}
              />
            ))}
            {imageUrls.length > 3 && (
              <Text type="secondary">+{imageUrls.length - 3} more</Text>
            )}
          </Image.PreviewGroup>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="Record Sale">
            <Button
              type="primary"
              icon={<MoneyCollectOutlined />}
              onClick={() => handleSellCollateral(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="View Details">
            <Button
              icon={<EyeOutlined />}
              onClick={() => window.open(`/collateral/${record.id}`, "_blank")}
              size="small"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const soldColumns = [
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          {record.loan && (
            <div>
              <Text type="secondary">
                Loan: {record.loan.borrower?.first_name}{" "}
                {record.loan.borrower?.last_name}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Initial Value",
      dataIndex: "valuation_amount",
      key: "valuation_amount",
      render: (amount) => (
        <Text>ZMW {parseFloat(amount || 0).toLocaleString()}</Text>
      ),
    },
    {
      title: "Sale Amount",
      dataIndex: "sale_amount",
      key: "sale_amount",
      render: (amount) => (
        <Text strong style={{ color: "#52c41a" }}>
          ZMW {parseFloat(amount || 0).toLocaleString()}
        </Text>
      ),
    },
    {
      title: "Profit/Loss",
      dataIndex: "profit_loss",
      key: "profit_loss",
      render: (amount) => (
        <Text style={{ color: amount >= 0 ? "#3f8600" : "#cf1322" }}>
          ZMW {parseFloat(amount || 0).toLocaleString()}
        </Text>
      ),
    },
    {
      title: "Date Sold",
      dataIndex: "date_sold",
      key: "date_sold",
      render: (date) => dayjs(date).format("DD/MM/YYYY"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={statusColors[status] || "default"}>{status}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              icon={<EyeOutlined />}
              onClick={() => window.open(`/collateral/${record.id}`, "_blank")}
              size="small"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <LineWave height="100" width="100" color="#1890ff" />
        <p>Loading collateral sales data...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card>
            <Title level={2} style={{ margin: 0 }}>
              <MoneyCollectOutlined style={{ marginRight: 8 }} />
              Collateral Sales Management
            </Title>
            <Text type="secondary">
              Manage and record sales of collateral assets marked as sold
            </Text>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card>
            <Statistic
              title="Available for Sale"
              value={availableCollaterals.length}
              prefix={<DollarOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card>
            <Statistic
              title="Sold Collateral"
              value={collaterals.length}
              prefix={<DollarOutlined />}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card>
            <Statistic
              title="Total Sale Value"
              value={collaterals.reduce(
                (sum, col) => sum + parseFloat(col.sale_amount || 0),
                0
              )}
              prefix="ZMW"
              precision={2}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card>
            <Statistic
              title="Total Profit/Loss"
              value={collaterals.reduce(
                (sum, col) => sum + parseFloat(col.profit_loss || 0),
                0
              )}
              prefix="ZMW"
              precision={2}
              valueStyle={{
                color:
                  collaterals.reduce(
                    (sum, col) => sum + parseFloat(col.profit_loss || 0),
                    0
                  ) >= 0
                    ? "#3f8600"
                    : "#cf1322",
              }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Tabs defaultActiveKey="available" onChange={setActiveTab}>
          <TabPane tab="Available for Sale" key="available">
            <Table
              columns={columns}
              dataSource={availableCollaterals}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
              scroll={{ x: 800 }}
            />
          </TabPane>
          <TabPane tab="Sold Collateral" key="sold">
            <Table
              columns={soldColumns}
              dataSource={collaterals}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
              scroll={{ x: 800 }}
            />
          </TabPane>
        </Tabs>
      </Card>

      <Modal
        title={`Record Sale - ${selectedCollateral?.description}`}
        open={isModalVisible}
        onCancel={handleModalCancel}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            date_sold: dayjs(),
          }}
        >
          <Form.Item
            name="sale_amount"
            label="Sale Amount (ZMW)"
            rules={[
              { required: true, message: "Please enter the sale amount" },
              { type: "number", min: 0, message: "Amount must be positive" },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              precision={2}
              placeholder="Enter sale amount"
            />
          </Form.Item>

          <Form.Item
            name="related_loan_id"
            label="Related Loan"
            rules={[
              { required: true, message: "Please select the related loan" },
            ]}
          >
            <Select placeholder="Select related loan" showSearch>
              {loans.map((loan) => (
                <Option key={loan.id} value={loan.id}>
                  Loan #{loan.id} - {loan.borrower?.first_name}{" "}
                  {loan.borrower?.last_name}
                  (ZMW {parseFloat(loan.amount).toLocaleString()})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="account_id"
            label="Credit Account"
            rules={[{ required: true, message: "Please select an account" }]}
          >
            <Select placeholder="Select account to credit">
              {accounts.map((account) => (
                <Option key={account.id} value={account.id}>
                  {account.name} - Balance: ZMW{" "}
                  {parseFloat(account.balance || 0).toLocaleString()}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="date_sold"
            label="Date Sold"
            rules={[{ required: true, message: "Please select the sale date" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="summary"
            label="Sale Summary"
            rules={[{ required: true, message: "Please enter a sale summary" }]}
          >
            <TextArea
              rows={4}
              placeholder="Enter details about the sale, buyer information, profit/loss, etc."
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={processing}>
                Record Sale
              </Button>
              <Button onClick={handleModalCancel}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CollateralSales;
